"use client";

import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'EN' | 'AR';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Record<string, string>> = {
  EN: {
    // Header
    'header.whatsapp': 'WhatsApp',
    
    // Hero Section
    'hero.headline1': 'Premium Women\'s',
    'hero.headline2': 'Hair Extensions',
    'hero.subtitle': 'Scandinavian luxury meets Dubai elegance. 100% Remy European hair extensions crafted exclusively for women. Located at Azizi Riviera 7, Meydan.',
    'hero.bookAppointment': 'Book Appointment',
    'hero.whatsappUs': 'WhatsApp Us',
    'hero.email': 'info@harextension.com',
    'hero.location': 'Azizi Riviera 7 Lagoon View Side, Meydan',
    
    // Pricing Section
    'pricing.byScandinavia': 'by SCANDINAVIA',
    'pricing.copenhagenCollection': 'COPENHAGEN COLLECTION',
    'pricing.50cm': '50 CM',
    'pricing.nordicBase': 'NORDIC BASE',
    'pricing.55cm': '55 CM',
    'pricing.scandinavianSignature': 'SCANDINAVIAN SIGNATURE',
    'pricing.60cm': '60 CM',
    'pricing.nordicLength': 'NORDIC LENGTH',
    'pricing.addVolume': 'ADD VOLUME',
    'pricing.fjordVolume': 'Fjord Volume (50g)',
    'pricing.arcticFull': 'Arctic Full (100g)',
    'pricing.scandicVolumeBoost': 'SCANDIC VOLUME BOOST',
    'pricing.scandicVolumeBoostDesc': 'For clients who want fuller, natural-looking hair without added length',
    'pricing.customLengths': 'CUSTOM LENGTHS',
    'pricing.over60cm': 'Over 60cm',
    'pricing.customQuote': 'Custom Quote',
    'pricing.refitMaintenance': 'REFIT & MAINTENANCE',
    'pricing.geniusWeft': 'Genius Weft / Flet (Per row)',
    'pricing.butterflyMethod': 'Butterfly Method (Standard refit)',
    'pricing.fullRefit': 'Full Refit (Complete repositioning)',
    'pricing.footerNote1': 'All prices include consultation, application, and styling',
    'pricing.footerNote2': '100% Remy European Double Drawn Hair · Prices in AED inclusive of VAT',
    
    // Booking Section
    'booking.title': 'Book Appointment',
    'booking.headline1': 'Reserve Your',
    'booking.headline2': 'Session',
    'booking.subtitle': 'Select your package, service, date, and time. We\'ll confirm your appointment via WhatsApp within 2 hours.',
    'booking.choosePackage': 'Choose Your Package *',
    'booking.selectPackage': 'Select a package from Copenhagen Collection',
    'booking.50cmNordicEssential': '50 CM - Nordic Essential Collection',
    'booking.55cmScandinavianSignature': '55 CM - Scandinavian Signature Collection',
    'booking.60cmNordicCouture': '60 CM - Nordic Couture Collection',
    'booking.refitMaintenanceServices': 'Refit & Maintenance Services',
    'booking.chooseService': 'Choose a Service',
    'booking.freeConsultation': 'Free Consultation',
    'booking.consultationDesc': '30-min session to find your perfect match',
    'booking.newExtensions': 'New Extensions',
    'booking.newExtensionsDesc': 'Full application of your chosen collection',
    'booking.refitService': 'Refit Service',
    'booking.refitServiceDesc': 'Repositioning your existing extensions',
    'booking.maintenance': 'Maintenance',
    'booking.maintenanceDesc': 'Butterfly method or row maintenance',
    'booking.selectDate': 'Select Date *',
    'booking.selectTime': 'Select Time *',
    'booking.chooseTimeSlot': 'Choose a time slot',
    'booking.yourName': 'Your Name *',
    'booking.enterName': 'Enter your full name',
    'booking.phoneNumber': 'Phone Number *',
    'booking.phonePlaceholder': '+971 XX XXX XXXX',
    'booking.additionalNotes': 'Additional Notes',
    'booking.notesPlaceholder': 'Any special requests or questions?',
    'booking.confirmWhatsapp': 'Confirm via WhatsApp',
    'booking.fillRequired': 'Please fill all required fields',
    
    // Footer
    'footer.phone': '+971 58 567 5600',
    'footer.email': 'info@harextension.com',
    'footer.location': 'Azizi Riviera 7 Lagoon View Side, Meydan, Dubai',
    'footer.copyright': '© 2025 HÅR Extension. All rights reserved.',
    'footer.privacyPolicy': 'Privacy Policy',
    'footer.terms': 'Terms',
    
    // Reviews Section
    'reviews.title': 'Client Reviews',
    'reviews.headline1': 'What Our',
    'reviews.headline2': 'Clients Say',
    'reviews.subtitle': 'Discover why women in Dubai trust us for exceptional hair transformations',
  },
  AR: {
    // Header
    'header.whatsapp': 'واتساب',
    
    // Hero Section
    'hero.headline1': 'وصلات شعر فاخرة',
    'hero.headline2': 'للنساء',
    'hero.subtitle': 'الفخامة الاسكندنافية تلتقي بأناقة دبي. وصلات شعر أوروبية ريمي 100٪ مصنوعة حصريًا للنساء. تقع في أزيزي ريفييرا 7، ميدان.',
    'hero.bookAppointment': 'احجز موعد',
    'hero.whatsappUs': 'تواصل عبر واتساب',
    'hero.email': 'info@harextension.com',
    'hero.location': 'أزيزي ريفييرا 7، ريتيل 5، ميدان',
    
    // Pricing Section
    'pricing.byScandinavia': 'من إسكندنافيا',
    'pricing.copenhagenCollection': 'مجموعة كوبنهاغن',
    'pricing.50cm': '50 سم',
    'pricing.nordicBase': 'نورديك بيس',
    'pricing.55cm': '55 سم',
    'pricing.scandinavianSignature': 'التوقيع الاسكندنافي',
    'pricing.60cm': '60 سم',
    'pricing.nordicLength': 'نورديك لينث',
    'pricing.addVolume': 'إضافة حجم',
    'pricing.fjordVolume': 'فيورد فوليوم (50 جم)',
    'pricing.arcticFull': 'أركتيك فول (100 جم)',
    'pricing.scandicVolumeBoost': 'سكانديك فوليوم بوست',
    'pricing.scandicVolumeBoostDesc': 'للعملاء الذين يريدون شعراً أكثر امتلاءً وطبيعية دون إضافة طول',
    'pricing.customLengths': 'أطوال مخصصة',
    'pricing.over60cm': 'أكثر من 60 سم',
    'pricing.customQuote': 'عرض سعر مخصص',
    'pricing.refitMaintenance': 'إعادة التركيب والصيانة',
    'pricing.geniusWeft': 'جينيوس ويفت / فليت (لكل صف)',
    'pricing.butterflyMethod': 'طريقة الفراشة (إعادة تركيب قياسية)',
    'pricing.fullRefit': 'إعادة تركيب كاملة (إعادة وضع كاملة)',
    'pricing.footerNote1': 'جميع الأسعار تشمل الاستشارة والتطبيق والتصفيف',
    'pricing.footerNote2': 'شعر أوروبي ريمي مزدوج السحب 100٪ · الأسعار بالدرهم الإماراتي شاملة ضريبة القيمة المضافة',
    
    // Booking Section
    'booking.title': 'حجز موعد',
    'booking.headline1': 'احجز',
    'booking.headline2': 'جلستك',
    'booking.subtitle': 'اختر الباقة والخدمة والتاريخ والوقت. سنؤكد موعدك عبر واتساب خلال ساعتين.',
    'booking.choosePackage': 'اختر الباقة *',
    'booking.selectPackage': 'اختر باقة من مجموعة كوبنهاغن',
    'booking.50cmNordicEssential': '50 سم - مجموعة نورديك الأساسية',
    'booking.55cmScandinavianSignature': '55 سم - مجموعة التوقيع الاسكندنافي',
    'booking.60cmNordicCouture': '60 سم - مجموعة نورديك كوتور',
    'booking.refitMaintenanceServices': 'خدمات إعادة التركيب والصيانة',
    'booking.chooseService': 'اختر الخدمة',
    'booking.freeConsultation': 'استشارة مجانية',
    'booking.consultationDesc': 'جلسة 30 دقيقة للعثور على المناسب لك',
    'booking.newExtensions': 'وصلات جديدة',
    'booking.newExtensionsDesc': 'تطبيق كامل للمجموعة المختارة',
    'booking.refitService': 'خدمة إعادة التركيب',
    'booking.refitServiceDesc': 'إعادة وضع الوصلات الحالية',
    'booking.maintenance': 'صيانة',
    'booking.maintenanceDesc': 'طريقة الفراشة أو صيانة الصف',
    'booking.selectDate': 'اختر التاريخ *',
    'booking.selectTime': 'اختر الوقت *',
    'booking.chooseTimeSlot': 'اختر وقت الموعد',
    'booking.yourName': 'اسمك *',
    'booking.enterName': 'أدخل اسمك الكامل',
    'booking.phoneNumber': 'رقم الهاتف *',
    'booking.phonePlaceholder': '+971 XX XXX XXXX',
    'booking.additionalNotes': 'ملاحظات إضافية',
    'booking.notesPlaceholder': 'أي طلبات خاصة أو أسئلة؟',
    'booking.confirmWhatsapp': 'تأكيد عبر واتساب',
    'booking.fillRequired': 'يرجى ملء جميع الحقول المطلوبة',
    
    // Footer
    'footer.phone': '5600 567 58 971+',
    'footer.email': 'info@harextension.com',
    'footer.location': 'أزيزي ريفييرا 7، ريتيل 5، ميدان، دبي',
    'footer.copyright': '© 2025 HÅR Extension. جميع الحقوق محفوظة.',
    'footer.privacyPolicy': 'سياسة الخصوصية',
    'footer.terms': 'الشروط',
    
    // Reviews Section
    'reviews.title': 'آراء العملاء',
    'reviews.headline1': 'ما يقوله',
    'reviews.headline2': 'عملاؤنا',
    'reviews.subtitle': 'اكتشف لماذا تثق النساء في دبي بنا لتحقيق تحولات شعر استثنائية',
  },
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('EN');

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'EN' ? 'AR' : 'EN'));
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}